import { useQuery } from '@tanstack/react-query'
import { HandHeart } from 'lucide-react'
import { coverSummary, coverWhen, fetchMyCover, type CoverRow } from '../api/cover'

/**
 * Cover, on the screen she lands on (D-365).
 *
 * **Owner:** *"i think burying too much stuff with scrolling on the schedule
 * page which is arguably the longest page for teachers especially that are
 * teaching a bunch of classes is bad design and they wouldn't see it. so i feel
 * like it should prob live on the home page. when there is something to display
 * in the cover section we display it else show nothing."*
 *
 * **Above the today list, not below it — and that is a change to his order.**
 * `todaySessions` is uncapped and includes privates and room bookings, so a
 * twelve-session Monday is roughly 1,680px of cards; placing cover after them
 * puts it at the same depth D-352 measured as the burying problem this is
 * escaping. It sits with the signals already above, which is where home puts
 * everything waiting on her.
 *
 * **A section with cards, never a stack of `AttentionBanner`s.** A banner wraps
 * its whole row in a link, so Yes/No inside one is invalid markup and
 * unreachable by keyboard — and its own rule is *one banner per kind, counted,
 * never a stack* (D-288), which cannot express three separate answers.
 *
 * **Two kinds of fact, and only two.** What somebody has asked of *her* is
 * answerable here, because it commits her evening and nothing proceeds until
 * she answers. What *she* has declared and not resolved is a quiet line that
 * opens the day dialog on that date — the arranging happens where she declared
 * it, which keeps the one irreversible sixty-household action (D-356) off the
 * screen she opens every day.
 *
 * **What she has agreed to take for somebody else is deliberately not here.**
 * That belongs on her week, beside the class, and a substitute badge says so
 * there. Her own absences are different and all of them are here, including
 * the ones already covered — see the filter below for why.
 */
/**
 * Whether this panel has anything to show (D-415).
 *
 * <b>Exported so a heading above it can follow.</b> This component's own rule
 * is written two lines from where it returns — *"nothing to say, so nothing is
 * said. No empty heading."* — and a caller that wants to title it had no way to
 * ask. That is how a dual-role account ended up with *Needs an answer* over
 * empty space.
 *
 * The same query key, so it costs no extra request, and the two filters are the
 * ones the panel itself renders from.
 */
export function useCoverWaiting(): boolean {
  const { data } = useQuery({ queryKey: ['cover', 'mine'], queryFn: fetchMyCover })
  const rows = data ?? []

  // What is asked *of* her is an ask on the one list now (D-543, D-552),
  // answered by the same row a teacher answers on /asks - this panel keeps
  // only her own absences, which are subjects and not asks.

  return rows.some((r) => r.askedOfMe && !r.isCancelled) || rows.some(stillMine)
}

/**
 * A night of her own that is still going somewhere.
 *
 * **One predicate, because it was two identical ones in this file** — the
 * summary above and the list below — and adding `AwaitingApproval` (D-540) to
 * one and not the other is precisely the drift that makes a row visible in a
 * heading and missing from the list under it.
 *
 * **`AwaitingApproval` belongs here, and leaving it out was found by an
 * audit.** Hannah declares out, asks Ava, Ava says yes — and the row failed
 * both filters, so the panel went from *"Waiting on Ava"* to nothing at all.
 * That is the same bug this block's own comment records being fixed once
 * before for `Accepted`: the one outcome she could not learn was the one that
 * means it worked.
 *
 * `Withdrawn` is the only live state left out, and rightly: she is coming after
 * all, so there is nothing to tell her.
 */
function stillMine(r: CoverRow): boolean {
  return (
    r.iAmAbsent &&
    !r.isCancelled &&
    (r.state === 'Uncovered' ||
      r.state === 'Asked' ||
      r.state === 'AwaitingApproval' ||
      r.state === 'Accepted')
  )
}

export default function CoverOnHome({ onOpenDay }: { onOpenDay: (date: string) => void }) {

  /*
   * **Its own query, and a failure here never takes home down with it** (D-209).
   *
   * What she came to home for is tonight's classes. No cover and a failed
   * lookup both render nothing, which is the honest reading of "we do not
   * know" — the alternative is a heading over an empty box that reads as
   * *nobody needs you*.
   */
  const { data } = useQuery({ queryKey: ['cover', 'mine'], queryFn: fetchMyCover })

  const rows = data ?? []

  /*
   * **Every absence of hers that is still live — including the ones somebody
   * has agreed to take.**
   *
   * The first cut filtered `Accepted` out, on the reasoning that it is not a
   * job: somebody is taking it and there is nothing left for her to do. That
   * was wrong, and wrong in the direction that matters. `observed`: once cover
   * is accepted, `CalendarService` **replaces** the class's instructor list
   * with the substitute alone, and a staff calendar is scoped by
   * `Instructors.Any(i => i.Id == staffId)` — so the class **leaves the absent
   * teacher's week entirely**. There is no accept notification either; the only
   * `cover.*` template that dispatches is `cover.asked`.
   *
   * So she would have had no screen anywhere telling her the answer was yes.
   * A decline surfaces (the row returns to `Uncovered` and the line says the
   * last person asked said no); an accept did not. **The one outcome she could
   * not learn was the one that means it worked.**
   *
   * `Withdrawn` is genuinely over and stays out.
   */
  const mine = rows.filter(stillMine)

  // Nothing to say, so nothing is said. No empty heading.
  if (mine.length === 0) return null

  return (
    <section className="mb-4 rounded-xl border border-line bg-surface px-4 py-3">
      {/*
        **It said *Cover*, and a bare noun does not say which way round** (D-396).
        `asked`: *"the cover labelling is unclear."* The section holds two
        opposite things — somebody asking her to take a class, and a class of
        her own still needing somebody — and one word covering both told her
        neither. The question it actually answers is who ends up teaching, so
        it says that.

        The rest of the vocabulary stays *cover* (*Needs cover*, *Uncovered*,
        *Nobody could cover it*): renaming one heading and leaving eight other
        strings is how a screen ends up using two words for one idea.
      */}
      <h2 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        <HandHeart size={13} strokeWidth={2.5} />
        Who is covering
      </h2>

{/*
        **A line, not a card, and it opens the dialog on that date** (D-365).
        The arranging lives where she declared it; this only has to tell her
        there is something left to do and take her to it. D-360's fault shape is
        a link that lands somewhere unable to answer — so it carries the date.
      */}
      {mine.map((row) => (
        <button
          key={row.id}
          type="button"
          onClick={() => onOpenDay(row.date)}
          className="-mx-1 flex w-full items-baseline gap-2 rounded-lg px-1 py-1.5 text-left hover:bg-surface-2"
        >
          {/*
            **Which class, and it was missing.** The line said the day and the
            state and nothing else, so a teacher out for two classes on one
            Thursday got two identical rows with no way to tell them apart —
            and tapping either opened the same day, leaving her to work out
            which was which. Found by a mutation check: a test meant to prove a
            row is not listed twice could not, because there was nothing on the
            line unique to the row.

            `wrap-anywhere` because a Jackrabbit name is one unbreakable word to
            CSS and the distinguishing part is at the end (D-115).
          */}
          <span className="min-w-0 flex-1">
            <span className="block text-[12.5px] font-semibold wrap-anywhere text-ink-2">
              {row.className}
            </span>
            <span className="block text-[12px] text-ink-3">
              {coverWhen(row)} &middot; {coverSummary(row, true)}
            </span>
          </span>
        </button>
      ))}
    </section>
  )
}
